<template>
  <canvas
    ref="drawCanvas"
    class="draw-canvas"
  />
</template>

<script>
import fabric from '@/vendor/fabric'
import { createArrowHeadSegments } from './drawArrow'

export default {
  name: 'DrawOverlayCanvas',
  props: {
    visible: Boolean,
    width: {
      type: Number,
      default: 0,
    },
    height: {
      type: Number,
      default: 0,
    },
    tool: {
      type: String,
      default: 'select', // select | rect | ellipse | free | text | arrow
    },
    stroke: {
      type: String,
      default: '#ff0000',
    },
    strokeWidth: {
      type: Number,
      default: 3,
    },
    rotation: {
      type: Number,
      default: 0,
    },
    backgroundColor: {
      type: String,
      default: 'transparent',
    },
  },
  data () {
    return {
      canvas: null,
      isPointerDown: false,
      startPoint: null,
      currentObject: null,
      hasChanges: false,
      textEditing: false,
      freeBrush: null,
      history: [],
      historyIndex: -1,
      isApplyingHistory: false,
      freeStrokeTimer: null,
      originalGetPointer: null,
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.ensureCanvas()
        this.resize()
        this.applyTool()
      } else {
        // 不主动清空，退出画图模式时由外部调用 reset/discard
        if (this.canvas) {
          this.canvas.isDrawingMode = false
        }
      }
    },
    width () {
      this.resize()
    },
    height () {
      this.resize()
    },
    rotation () {
      // 外层 CSS 旋转后，重新计算 canvas 偏移，保证指针映射正确
      this.$nextTick(() => {
        if (this.canvas && this.canvas.calcOffset) {
          this.canvas.calcOffset()
        }
      })
    },
    tool () {
      this.textEditing = false
      this.applyTool()
    },
    stroke () {
      this.applyTool()
    },
    strokeWidth () {
      this.applyTool()
    },
  },
  mounted () {
    this.ensureCanvas()
    this.resize()
    this.applyTool()
  },
  beforeDestroy () {
    if (this.canvas) {
      this.canvas.off('keydown', this.onCanvasKeydown)
      if (this.originalGetPointer) {
        this.canvas.getPointer = this.originalGetPointer
        this.originalGetPointer = null
      }
      this.canvas.dispose && this.canvas.dispose()
    }
    this.canvas = null
  },
  methods: {
    ensureCanvas () {
      if (this.canvas) return
      this.canvas = new fabric.Canvas(this.$refs.drawCanvas, {
        selection: true,
        preserveObjectStacking: true,
      })

      // 画笔默认只在 free 模式启用
      this.freeBrush = new fabric.PencilBrush(this.canvas)
      this.freeBrush.color = this.stroke
      this.freeBrush.width = this.strokeWidth

      // 外层标注容器会 CSS 缩放/旋转，覆盖 getPointer 做逆变换
      this.originalGetPointer = this.canvas.getPointer.bind(this.canvas)
      this.canvas.getPointer = (e, ignoreZoom) => {
        return this.getRotatedPointer(e, ignoreZoom)
      }

      this.canvas.on('mouse:down', this.onMouseDown)
      this.canvas.on('mouse:move', this.onMouseMove)
      this.canvas.on('mouse:up', this.onMouseUp)
      this.canvas.on('keydown', this.onCanvasKeydown)

      // 兼容不同 fabric 版本的事件命名
      this.canvas.on('path:created', () => {
        this.hasChanges = true
        this.pushHistory('free')
      })
    },

    normalizeRotation (degree) {
      return ((Number(degree) || 0) % 360 + 360) % 360
    },

    /**
     * 外层 CSS scale/rotate 后，将 client 坐标映射回 fabric 内部坐标系
     */
    getRotatedPointer (e, ignoreZoom) {
      if (!this.canvas) {
        return this.originalGetPointer ? this.originalGetPointer(e, ignoreZoom) : {x: 0, y: 0}
      }

      const evt = e.e || e
      const clientX = evt.touches && evt.touches[0] ? evt.touches[0].clientX : evt.clientX
      const clientY = evt.touches && evt.touches[0] ? evt.touches[0].clientY : evt.clientY
      if (typeof clientX !== 'number' || typeof clientY !== 'number') {
        return this.originalGetPointer(e, ignoreZoom)
      }

      const el = this.canvas.upperCanvasEl || this.$refs.drawCanvas
      const rect = el.getBoundingClientRect()
      const canvasWidth = this.canvas.getWidth ? this.canvas.getWidth() : this.width
      const canvasHeight = this.canvas.getHeight ? this.canvas.getHeight() : this.height
      if (!canvasWidth || !canvasHeight) {
        return this.originalGetPointer(e, ignoreZoom)
      }

      const rotation = this.normalizeRotation(this.rotation)
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const dx = clientX - centerX
      const dy = clientY - centerY
      // CSS rotate 为顺时针，这里做逆变换还原到元素本地坐标
      const rad = -rotation * Math.PI / 180
      const cos = Math.cos(rad)
      const sin = Math.sin(rad)
      const localX = dx * cos - dy * sin
      const localY = dx * sin + dy * cos

      // 未旋转时的 CSS 显示尺寸（offset*），映射到 fabric 内部像素
      const displayWidth = el.offsetWidth || this.width || canvasWidth
      const displayHeight = el.offsetHeight || this.height || canvasHeight
      if (!displayWidth || !displayHeight) {
        return this.originalGetPointer(e, ignoreZoom)
      }

      return {
        x: localX * (canvasWidth / displayWidth) + canvasWidth / 2,
        y: localY * (canvasHeight / displayHeight) + canvasHeight / 2,
      }
    },

    onCanvasKeydown (opt) {
      if (!this.textEditing) return
      const obj = this.canvas.getActiveObject()
      if (!obj || typeof obj.get !== 'function') return
      const text = obj.get('text')
      if (text !== '请输入文字') return
      const e = opt.e
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        obj.set('text', e.key)
        e.preventDefault()
        e.stopPropagation()
        this.canvas.requestRenderAll()
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        obj.set('text', '')
        e.preventDefault()
        e.stopPropagation()
        this.canvas.requestRenderAll()
      }
    },

    resize () {
      if (!this.canvas) return
      const w = Number(this.width) || 0
      const h = Number(this.height) || 0
      const oldW = this.canvas.getWidth ? this.canvas.getWidth() : 0
      const oldH = this.canvas.getHeight ? this.canvas.getHeight() : 0
      // 尺寸锁定后不再改内部坐标系，避免旋转适配时重绘对象
      if (w === oldW && h === oldH) {
        this.canvas.requestRenderAll()
        return
      }
      if (!w || !h) return

      this.canvas.setWidth(w)
      this.canvas.setHeight(h)
      this.canvas.setBackgroundColor(this.backgroundColor, this.canvas.renderAll.bind(this.canvas))
      this.canvas.requestRenderAll()
    },

    reset () {
      this.hasChanges = false
      if (!this.canvas) return
      this.canvas.getObjects().forEach(obj => this.canvas.remove(obj))
      this.canvas.discardActiveObject && this.canvas.discardActiveObject()
      this.initHistory()
      this.pushHistory('reset', true)
      this.canvas.requestRenderAll()
    },

    discardChanges () {
      this.reset()
    },

    hasDrawn () {
      return this.hasChanges
    },

    isTextEditing () {
      return this.textEditing
    },

    applyTool () {
      if (!this.canvas) return
      if (!this.visible) return

      // 切模式时先停掉文本编辑状态
      if (this.textEditing && this.canvas.getActiveObject && this.canvas.getActiveObject()) {
        try {
          this.canvas.getActiveObject().exitEditing && this.canvas.getActiveObject().exitEditing()
        } catch (e) {
          // ignore
        }
      }
      this.textEditing = false

      const tool = this.tool
      const isSelect = tool === 'select'

      if (tool === 'free') {
        this.canvas.isDrawingMode = true
        this.freeBrush.color = this.stroke
        this.freeBrush.width = this.strokeWidth
        this.canvas.freeDrawingBrush = this.freeBrush
        this.canvas.selection = false
        return
      }

      this.canvas.isDrawingMode = false
      this.canvas.selection = isSelect

      // 工具状态下的对象可交互性：仅 select 时可选中/拖动
      this.canvas.getObjects().forEach((obj) => {
        obj.selectable = isSelect
        obj.evented = isSelect
        obj.hasControls = false
        obj.hasBorders = false
        obj.lockRotation = true
        obj.lockScalingX = true
        obj.lockScalingY = true
      })

      this.canvas.requestRenderAll()
    },

    emitHistoryChange () {
      const canUndo = this.historyIndex > 0
      const canRedo = this.historyIndex >= 0 && this.historyIndex < this.history.length - 1
      this.$emit && this.$emit('history-change', {canUndo, canRedo})
    },

    initHistory () {
      this.history = []
      this.historyIndex = -1
    },

    getCanvasState () {
      // 仅保存当前对象层；不保存透明背景色也没关系
      return this.canvas.toJSON()
    },

    pushHistory (reason, force = false) {
      if (!this.canvas) return
      if (this.isApplyingHistory && !force) return

      // freeDrawing 可能在一次 stroke 内多次触发，做个轻量 debounce
      if (reason === 'free') {
        clearTimeout(this.freeStrokeTimer)
        this.freeStrokeTimer = setTimeout(() => {
          this.pushHistory('free-inner', force)
        }, 120)
        return
      }

      // 若撤销过，再继续操作需要截断 redo 分支
      if (this.historyIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.historyIndex + 1)
      }

      const state = this.getCanvasState()
      this.history.push(state)
      this.historyIndex++
      this.updateHasChangesByObjects()
      this.emitHistoryChange()
    },

    updateHasChangesByObjects () {
      if (!this.canvas) {
        this.hasChanges = false
        return
      }
      const objects = this.canvas.getObjects ? this.canvas.getObjects() : []
      this.hasChanges = !!(objects && objects.length)
    },

    applyCanvasState (json) {
      if (!this.canvas) return Promise.resolve()

      this.isApplyingHistory = true
      this.canvas.clear()
      return new Promise((resolve) => {
        this.canvas.loadFromJSON(json, () => {
          this.isApplyingHistory = false
          this.updateHasChangesByObjects()
          // clear() 会移除背景色，undo/redo 后需要重新设置
          this.canvas.setBackgroundColor(this.backgroundColor, this.canvas.renderAll.bind(this.canvas))
          this.canvas.requestRenderAll()
          this.emitHistoryChange()
          resolve()
        })
      })
    },

    undo () {
      if (!this.canUndo()) return
      this.historyIndex--
      return this.applyCanvasState(this.history[this.historyIndex])
    },

    redo () {
      if (!this.canRedo()) return
      this.historyIndex++
      return this.applyCanvasState(this.history[this.historyIndex])
    },

    canUndo () {
      return this.historyIndex > 0
    },

    canRedo () {
      return this.historyIndex >= 0 && this.historyIndex < this.history.length - 1
    },

    getPointerInCanvas (opt) {
      const pointer = this.canvas.getPointer(opt.e)
      return {x: pointer.x, y: pointer.y}
    },

    onMouseDown (opt) {
      if (!this.visible) return
      if (this.tool === 'free') return
      if (this.textEditing) return

      const tool = this.tool
      if (tool === 'select') return

      this.isPointerDown = true
      this.startPoint = this.getPointerInCanvas(opt)

      if (tool === 'rect') {
        this.currentObject = new fabric.Rect({
          left: this.startPoint.x,
          top: this.startPoint.y,
          width: 0,
          height: 0,
          fill: 'rgba(0,0,0,0)',
          stroke: this.stroke,
          strokeWidth: this.strokeWidth,
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
        })
        this.canvas.add(this.currentObject)
      } else if (tool === 'arrow') {
        const endPoint = {
          x: this.startPoint.x,
          y: this.startPoint.y,
        }
        const line = new fabric.Line([
          this.startPoint.x,
          this.startPoint.y,
          endPoint.x,
          endPoint.y,
        ], {
          fill: this.stroke,
          stroke: this.stroke,
          strokeWidth: this.strokeWidth,
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
        })
        const headSegments = createArrowHeadSegments(this.startPoint, endPoint)
        const headLines = headSegments.map(segment => new fabric.Line([
          segment[0].x,
          segment[0].y,
          segment[1].x,
          segment[1].y,
        ], {
          fill: this.stroke,
          stroke: this.stroke,
          strokeWidth: this.strokeWidth,
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
        }))
        this.currentObject = {line, headLines}
        this.canvas.add(line)
        headLines.forEach(headLine => this.canvas.add(headLine))
      } else if (tool === 'ellipse') {
        this.currentObject = new fabric.Ellipse({
          left: this.startPoint.x,
          top: this.startPoint.y,
          originX: 'left',
          originY: 'top',
          rx: 0,
          ry: 0,
          fill: 'rgba(0,0,0,0)',
          stroke: this.stroke,
          strokeWidth: this.strokeWidth,
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
          lockRotation: true,
        })
        this.canvas.add(this.currentObject)
      } else if (tool === 'circle') {
        // 兼容旧调用；circle 行为等价于 shift 绘制圆形
        this.currentObject = new fabric.Ellipse({
          left: this.startPoint.x,
          top: this.startPoint.y,
          originX: 'left',
          originY: 'top',
          rx: 0,
          ry: 0,
          fill: 'rgba(0,0,0,0)',
          stroke: this.stroke,
          strokeWidth: this.strokeWidth,
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
          lockRotation: true,
        })
        this.canvas.add(this.currentObject)
      } else if (tool === 'text') {
        const textbox = new fabric.Textbox('', {
          left: this.startPoint.x,
          top: this.startPoint.y,
          fontSize: 20,
          fill: this.stroke,
          originX: 'left',
          originY: 'top',
        })
        this.canvas.add(textbox)
        this.canvas.setActiveObject(textbox)
        this.hasChanges = true
        this.textEditing = true
        textbox.enterEditing && textbox.enterEditing()
        textbox.on('editing:exited', () => {
          this.textEditing = false
          this.pushHistory('text')
          this.applyTool()
        })
        textbox.on('modified', () => {
          this.hasChanges = true
        })
      }

      this.canvas.requestRenderAll()
    },

    onMouseMove (opt) {
      if (!this.visible) return
      if (!this.isPointerDown) return
      if (!this.currentObject) return
      const tool = this.tool
      if (!['rect', 'circle', 'ellipse', 'arrow'].includes(tool)) return

      const p = this.getPointerInCanvas(opt)
      const x1 = this.startPoint.x
      const y1 = this.startPoint.y
      const x2 = p.x
      const y2 = p.y
      const left = Math.min(x1, x2)
      const top = Math.min(y1, y2)
      const w = Math.abs(x2 - x1)
      const h = Math.abs(y2 - y1)

      if (tool === 'rect') {
        this.currentObject.set({
          left: left,
          top: top,
          width: w,
          height: h,
        })
      } else if (tool === 'ellipse') {
        // ellipse + shift => circle
        if (opt && opt.e && opt.e.shiftKey) {
          const d = Math.max(w, h)
          this.currentObject.set({
            left: left,
            top: top,
            rx: d / 2,
            ry: d / 2,
          })
        } else {
          this.currentObject.set({
            left: left,
            top: top,
            rx: w / 2,
            ry: h / 2,
          })
        }
      } else if (tool === 'circle') {
        const d = Math.max(w, h)
        this.currentObject.set({
          left: left,
          top: top,
          rx: d / 2,
          ry: d / 2,
        })
      } else if (tool === 'arrow') {
        this.currentObject.line.set({
          x1: x1,
          y1: y1,
          x2: x2,
          y2: y2,
        })
        const headSegments = createArrowHeadSegments(this.startPoint, p)
        this.currentObject.headLines.forEach((headLine, index) => {
          const segment = headSegments[index]
          headLine.set({
            x1: segment[0].x,
            y1: segment[0].y,
            x2: segment[1].x,
            y2: segment[1].y,
          })
          headLine.setCoords && headLine.setCoords()
        })
        this.currentObject.line.setCoords && this.currentObject.line.setCoords()
      }

      this.canvas.requestRenderAll()
    },

    onMouseUp () {
      if (!this.visible) return
      if (this.tool === 'free') return
      if (!this.isPointerDown) return

      this.isPointerDown = false
      if (this.currentObject) {
        this.hasChanges = true
        if (this.tool === 'arrow') {
          this.currentObject.line.set({
            selectable: false,
            evented: false,
          })
          this.currentObject.headLines.forEach(headLine => headLine.set({
            selectable: false,
            evented: false,
          }))
        } else {
          this.currentObject.set({
            selectable: false,
            evented: false,
          })
        }
        this.pushHistory('shape')
      }

      this.currentObject = null
      this.startPoint = null
      this.canvas.requestRenderAll()
    },

    getOverlayDataUrl () {
      if (!this.canvas) return Promise.resolve('')
      // 透明背景下用 png，避免颜色丢失
      return Promise.resolve(this.canvas.toDataURL({format: 'png'}))
    },
  },
}
</script>

<style lang="scss"
       scoped>
.draw-canvas {
  display: block;
}
</style>




// WEBPACK FOOTER //
// src/views/question-review/components/AnswerFileFullScreen/src/DrawOverlayCanvas.vue